/**
 * End-to-End Encryption for Chat Messages and File Transfers (Web)
 *
 * Compatible with the mobile implementation:
 * - Curve25519 (X25519) key agreement
 * - XSalsa20-Poly1305 (tweetnacl secretbox) for authenticated encryption
 */

import nacl from 'tweetnacl';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const toUint8 = (value) => (value instanceof Uint8Array ? value : new Uint8Array(value));

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const isBrowser = typeof window !== 'undefined';

export const isEncryptionAvailable = () => isBrowser;

export const generateKeyPair = async () => {
  const keyPair = nacl.box.keyPair();

  return {
    publicKey: bufferToBase64(keyPair.publicKey),
    privateKey: bufferToBase64(keyPair.secretKey),
  };
};

export const deriveSharedKey = async (privateKeyBase64, peerPublicKeyBase64) => {
  const privateKey = new Uint8Array(base64ToArrayBuffer(privateKeyBase64));
  const peerPublicKey = new Uint8Array(base64ToArrayBuffer(peerPublicKeyBase64));
  return nacl.box.before(peerPublicKey, privateKey);
};

export const encryptMessage = async (plaintext, sharedKey) => {
  const nonce = nacl.randomBytes(24);
  const plaintextBytes = textEncoder.encode(plaintext);
  const keyBytes = toUint8(sharedKey);
  const ciphertextBytes = nacl.secretbox(plaintextBytes, nonce, keyBytes);

  return {
    ciphertext: bufferToBase64(ciphertextBytes),
    iv: bufferToBase64(nonce),
  };
};

export const decryptMessage = async (ciphertextBase64, ivBase64, sharedKey) => {
  const ciphertextBytes = new Uint8Array(base64ToArrayBuffer(ciphertextBase64));
  const nonce = new Uint8Array(base64ToArrayBuffer(ivBase64));
  const keyBytes = toUint8(sharedKey);
  const plaintextBytes = nacl.secretbox.open(ciphertextBytes, nonce, keyBytes);
  if (!plaintextBytes) {
    throw new Error('Decryption failed - wrong key or corrupted data');
  }
  return textDecoder.decode(plaintextBytes);
};

export const encryptFile = async (fileData, sharedKey) => {
  const nonce = nacl.randomBytes(24);
  const keyBytes = toUint8(sharedKey);
  const dataBytes = new Uint8Array(fileData);
  const encryptedBytes = nacl.secretbox(dataBytes, nonce, keyBytes);

  // IMPORTANT: Create a new ArrayBuffer with exact size, not the underlying buffer
  // encryptedBytes.buffer may be larger than encryptedBytes.length due to Uint8Array's byteOffset
  const exactBuffer = encryptedBytes.buffer.slice(
    encryptedBytes.byteOffset,
    encryptedBytes.byteOffset + encryptedBytes.byteLength
  );

  return {
    encryptedData: exactBuffer,
    iv: bufferToBase64(nonce),
  };
};

export const decryptFile = async (encryptedData, ivBase64, sharedKey) => {
  const encryptedBytes = new Uint8Array(encryptedData);
  const nonce = new Uint8Array(base64ToArrayBuffer(ivBase64));
  const keyBytes = toUint8(sharedKey);
  const decryptedBytes = nacl.secretbox.open(encryptedBytes, nonce, keyBytes);
  if (!decryptedBytes) {
    return null;
  }
  // IMPORTANT: Create a new ArrayBuffer with exact size, not the underlying buffer
  // decryptedBytes.buffer may be larger than decryptedBytes.length due to Uint8Array's byteOffset
  return decryptedBytes.buffer.slice(
    decryptedBytes.byteOffset,
    decryptedBytes.byteOffset + decryptedBytes.byteLength
  );
};

export const createEncryptedMessage = async (text, sharedKey) => {
  const { ciphertext, iv } = await encryptMessage(text, sharedKey);
  return {
    encrypted: true,
    ciphertext,
    iv,
    version: 5,
  };
};

export const decryptEncryptedMessage = async (encryptedMessage, sharedKey) => {
  if (!encryptedMessage?.encrypted) {
    return encryptedMessage?.text || '';
  }
  // Let the error propagate so fallback logic can try other keys
  return decryptMessage(encryptedMessage.ciphertext, encryptedMessage.iv, sharedKey);
};
